import { useState, useEffect, useCallback } from 'react';
import { useApi } from '../services/api';
import { ClaimsFilterBar } from '../components/claims/ClaimsFilterBar';
import { ClaimsTable } from '../components/claims/ClaimsTable';
import { EditClaimModal } from '../components/claims/EditClaimModal';
import { EnrichedClaim, ClaimsFilters } from '../components/claims/types';

export default function MyClaimsPage() {
    const api = useApi();
    const [claims, setClaims] = useState<EnrichedClaim[]>([]);
    const [games, setGames] = useState<{ id: string; name: string }[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [editingClaim, setEditingClaim] = useState<EnrichedClaim | null>(null);
    const [filters, setFilters] = useState<ClaimsFilters>({
        gameId: '', startDate: '', endDate: '', status: ''
    });

    useEffect(() => { loadGames(); }, []);
    useEffect(() => { loadClaims(); }, [filters]);

    async function loadGames() {
        try {
            const result = await api.getGames();
            setGames((result.games || []).map((g: any) => ({ id: g.id, name: g.name })));
        } catch (error) { console.error('Failed to load games:', error); }
    }

    async function loadClaims() {
        try {
            setLoading(true);
            setError(null);
            const activeFilters = Object.fromEntries(
                Object.entries(filters).filter(([_, v]) => v !== '')
            );
            const result = await api.getMyClaims(Object.keys(activeFilters).length > 0 ? activeFilters : undefined);
            setClaims(result.claims || []);
        } catch (err) {
            console.error('Failed to load claims:', err);
            // Preserve existing claims so the table isn't wiped on transient errors
            setError('Failed to load claims. Please try again.');
        } finally { setLoading(false); }
    }

    async function handleSaveEdit(claimId: string, data: { description: string; evidenceUrls: string[]; imageUrls: string[] }) {
        try {
            await api.updateClaim(claimId, data);
        } catch (err) {
            throw err instanceof Error ? err : new Error('Failed to save changes');
        }
        // Refresh claims list; surface a distinct message if refresh fails
        try {
            await loadClaims();
        } catch {
            setError('Saved successfully, but failed to refresh the list.');
        }
    }

    const handleUploadFile = useCallback((file: File) => api.uploadFile(file), [api]);

    return (
        <div className="p-4 sm:p-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-text-primary mb-6">My Claims</h1>
            <ClaimsFilterBar games={games} filters={filters} onFilterChange={setFilters} />
            {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6">
                    <p className="text-red-400">{error}</p>
                    <button onClick={loadClaims} className="text-primary hover:opacity-80 text-sm mt-2">
                        Try again
                    </button>
                </div>
            )}
            <ClaimsTable claims={claims} loading={loading} emptyMessage="You haven't made any claims yet." onEdit={setEditingClaim} />
            <EditClaimModal
                isOpen={editingClaim !== null}
                onClose={() => setEditingClaim(null)}
                claim={editingClaim}
                onSave={handleSaveEdit}
                onUploadFile={handleUploadFile}
            />
        </div>
    );
}
